// LivestreamOverlayChatroom.jsx
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import UniversalComponent from '../component/UniversalComponent.jsx';

import { EMPTY_ARRAY } from '../resource/defaults.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { color, textColor } from '../style/variables.js';
import media from '../style/media.js';
import CryfaceImageSource from '../../img/img_empty_nofollower.svg';

export const LivestreamOverlayChatroom = ({
  streamId,
  chatIds = EMPTY_ARRAY,
  footerContent,
}) => {
  const { t } = useTranslation();
  const wrapperRef = useRef(null);
  const [isShadowShow, setIsShadowShow] = useState(false);

  useEffect(() => {
    const wrapperElement = wrapperRef.current;
    let lastIsShadowShow = false;
    const handleScroll = event => {
      const currentIsShadowShow = event.target.scrollTop !== 0;
      if (currentIsShadowShow !== lastIsShadowShow) {
        lastIsShadowShow = currentIsShadowShow;
        setIsShadowShow(currentIsShadowShow);
      }
    };
    wrapperElement.addEventListener('scroll', handleScroll, { capture: true });
    return () => {
      wrapperElement.removeEventListener('scroll', handleScroll, {
        capture: true,
      });
    };
  }, []);

  return (
    <StyledLivestreamOverlayChatroom>
      <OverlayTitle>
        {t('livestream_detail_live_chat', {
          ns: TranslationNamespace.LIVESTREAM,
        })}
      </OverlayTitle>
      <OverlayChatroomWrapper ref={wrapperRef}>
        {isShadowShow && <GradientDeco />}
        {chatIds.length > 0 ? (
          <UniversalComponent
            filename="Container_LiveStreamChatroom"
            streamId={streamId}
            hasMaxHeight={true}
            shouldAllowHorizontalSwipeInOverlay={true}
            importer={
              /* #__LOADABLE__ */ () =>
                import('../container/LiveStreamChatroom.js')
            }
          />
        ) : (
          <EmptyChatroomWrapper>
            <CryFaceIcon src={CryfaceImageSource} aria-hidden />
            <Description>
              {t('livestream_detail_live_chat_empty_hint', {
                ns: TranslationNamespace.GENERAL,
              })}
            </Description>
          </EmptyChatroomWrapper>
        )}
      </OverlayChatroomWrapper>
      {footerContent && (
        <OverlayChatroomButtonWrapper>
          {footerContent}
        </OverlayChatroomButtonWrapper>
      )}
    </StyledLivestreamOverlayChatroom>
  );
};

LivestreamOverlayChatroom.propTypes = {
  streamId: PropTypes.string.isRequired,
  chatIds: PropTypes.array,
  footerContent: PropTypes.node,
};

const StyledLivestreamOverlayChatroom = styled.div`
  height: 100%;
  width: 100%;
  background-color: ${color.grey[900]};
  padding: 16px;
  display: grid;
  grid-template-rows: auto 1fr auto;
  gap: 12px;
`;

const OverlayTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  color: ${color.white};
`;

const OverlayChatroomWrapper = styled.div`
  position: relative;
  height: 100%;
  overflow-y: hidden;
`;

const OverlayChatroomButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
`;

const EmptyChatroomWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
`;

const CryFaceIcon = styled.img`
  width: 120px;
  height: 120px;
  ${media.tablet`
    @media (orientation: landscape) {
      width: 80px;
      height: 80px;
    }
  `}
`;

const Description = styled.section`
  color: ${textColor.black};
  max-width: 100%;
  font-size: 14px;
  font-weight: 400;
  text-align: left;
  line-height: 1.5;
`;

const GradientDeco = styled.div`
  position: absolute;
  z-index: 4;
  top: 0;
  left: 0;
  width: 100%;
  height: 44px;
  background: linear-gradient(
    180deg,
    ${color.grey[900]} 30.32%,
    ${color.neutral['10_alpha_0']} 54.13%
  );
`;

export default LivestreamOverlayChatroom;
